import * as React from 'react';

import styled from 'styled-components';

import MotionOpacity from '@components/motion/motion-opacity';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import SimpleArtist from '@common/types/artist/simple-artist';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { Dispatch } from 'redux';

import Box from '@components/layout/box';
import { AnimatePresence } from 'framer-motion';
import Typography from '@components/basics/typography';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import Hint from '@components/basics/hint';
import { Translate } from 'react-localize-redux';
import UnselectedIcon from '@components/basics/icons/unselected';
import { SimpleRelease } from '@common/types/release/simple-release';
import ArtistEarningItem from '@webapp/controllers/artist/artist-earning-list/earning-item';
import DineroFactory from 'dinero.js';
import { ButtonLoadingCtrl } from '@webapp/controllers/button-loading';
import Button from '@components/actions/button';
import Flex from '@components/layout/flex';
import { EarningsResponse } from '@services/earnings/types/earnings-response';
import { getWidth } from '@components/styles';
import { RestoreDeactivatedArtist } from '@webapp/store/artist/action-creators';
import { Separator } from '@components/form/datepicker/components';

interface RestoreArtistPageProps {
  className?: string;
  artists: SimpleArtist[];
  releases: SimpleRelease[];
  earnings: Record<string, EarningsResponse[]>;
  artistId: string;
  isMobile: boolean;
  goToDashboard: () => CallHistoryMethodAction;
  restoreDeactivatedArtist: (
    artistId: string,
    releaseIds?: string[]
  ) => RestoreDeactivatedArtist;
}

interface Options {
  value: 'all releases' | 'selected releases';
  label: string;
}

const mapStateToProps = (state: AppState) => ({
  artistId: state.release.listDeactivatedArtist.id,
  artists: state.artist.myArtists.filter((artist) => !artist.isActive),
  releases:
    state.release.listDeactivatedArtist.releases.filter(
      (r) => r.status === 'takedown-pending' || r.status === 'taken-down'
    ) || [],
  earnings: state.earnings.data,
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goToDashboard: () => dispatch(push(`/dashboard`)),
  restoreDeactivatedArtist: (artistId: string, releaseIds?: string[]) =>
    dispatch(new RestoreDeactivatedArtist(artistId, releaseIds)),
});

export const RestoreArtistPage: React.FC<RestoreArtistPageProps> = (props) => {
  const { artists, artistId, goToDashboard } = props;
  const [selectedOption, setSelectedOption] =
    React.useState<Options['value']>('all releases');
  const [selectedReleases, setSelectedReleases] = React.useState<
    SimpleRelease[]
  >([]);

  const [restoreStarted, setRestoreStarted] = React.useState<boolean>(false);

  const artist = artists.find((artist) => artist.id === artistId);

  React.useEffect(() => {
    // this is for preventing user to restore artists that are not in the deactivated list
    if (artistId && !artists?.some((artist) => artist.id === artistId)) {
      goToDashboard();
    }
  }, [artists, artistId, goToDashboard]);

  const releasesWithEarnings = props.releases.map((release) => {
    const releaseEarnings = props.earnings[artistId]?.find(
      (earning) => earning.releaseId === release.id
    );
    if (releaseEarnings) {
      return {
        ...release,
        streams: (release.streams || 0) + releaseEarnings.streams,
        earnings: DineroFactory({
          amount: release.earnings || 0,
          precision: 5,
          currency: 'USD',
        })
          .add(releaseEarnings.total)
          .getAmount(),
      };
    }
    return release;
  });

  const totalStreams = releasesWithEarnings.reduce(
    (acc, release) => acc + (release.streams || 0),
    0
  );

  const totalEarnings = releasesWithEarnings.reduce(
    (acc, release) => acc + (release.earnings || 0),
    0
  );

  const handleOptionSelection = (value: Options['value']) => {
    setSelectedOption(value);
  };

  const handleSelectAllReleases = () => {
    if (selectedReleases.length === props.releases.length) {
      setSelectedReleases([]);
    } else {
      setSelectedReleases(props.releases);
    }
  };

  const handleReleaseSelection = (release: SimpleRelease) => {
    if (selectedReleases.some((r) => r.id === release.id)) {
      setSelectedReleases(selectedReleases.filter((r) => r.id !== release.id));
    } else {
      setSelectedReleases([...selectedReleases, release]);
    }
  };

  const handleContinue = () => {
    setRestoreStarted(true);
    props.restoreDeactivatedArtist(
      artistId,
      selectedOption === 'all releases'
        ? props.releases.map((r) => r.id)
        : selectedReleases.map((release) => release.id)
    );
  };

  const handleCancel = () => {
    goToDashboard();
  };

  const Options: Options[] = [
    {
      value: 'all releases',
      label: 'ALL.RELEASES',
    },
    {
      value: 'selected releases',
      label: 'SELECTED.RELEASES',
    },
  ];

  return (
    <PageContainer className={props.className || ''}>
      <Typography className="mb-30" type="subheader" customFont="semi-bold">
        <Translate
          id="ARTIST.RESTORE.OPTIONS.TITLE"
          data={{
            name: artist?.name || 'artist',
          }}
        />
      </Typography>
      <Box noBorderBottom>
        <Wrapper>
          <Typography
            className="mb-10"
            type="subheader"
            customFont="semi-bold"
            style={{ fontSize: '18px' }}
          >
            <Translate id="ARTIST.RESTORE.OPTIONS.SUBTITLE" />
          </Typography>
          {Options.map((option, i) => (
            <OptionBox
              className={`option ${
                option.value === selectedOption ? 'active' : ''
              } ${'no-border-bottom'}`}
              key={`option-box_${option.value}_${option.label}_${i}`}
              onClick={() => handleOptionSelection(option.value)}
            >
              <Typography weight="700">
                <Translate id={`ARTIST.RESTORE.OPTION.${option.label}`} />
              </Typography>
              <AnimatePresence key={`option_${option.value}_${i}`}>
                {i === 0 ? (
                  <Badge>
                    <Typography weight="700">
                      <Translate id="ARTIST.RESTORE.BADGE.RECOMMENDED" />
                    </Typography>
                  </Badge>
                ) : null}
                {option.value === selectedOption ? (
                  <MotionOpacity>
                    <CompletedIcon className="icon" />
                  </MotionOpacity>
                ) : (
                  <MotionOpacity>
                    <UnselectedIcon noAnimation />
                  </MotionOpacity>
                )}
              </AnimatePresence>
            </OptionBox>
          ))}
          {selectedOption === 'selected releases' && (
            <AnimatePresence key="selected-releases">
              <ReleasesWrapper>
                <Row className="item" onClick={handleSelectAllReleases}>
                  <ArtistEarningItem
                    className="item"
                    type=""
                    quantity={totalStreams}
                    earned={DineroFactory({
                      amount: totalEarnings,
                      precision: 5,
                      currency: 'USD',
                    })}
                    name="All Releases"
                    isDarkBg
                    isSelected={
                      selectedReleases.length === props.releases?.length
                    }
                  />
                  {selectedReleases.length === props.releases?.length ? (
                    <MotionOpacity>
                      <CompletedIcon size={32} className="icon" />
                    </MotionOpacity>
                  ) : (
                    <MotionOpacity>
                      <UnselectedIcon isWhite />
                    </MotionOpacity>
                  )}
                </Row>
                {releasesWithEarnings?.map((release, i) => {
                  return (
                    <>
                      {i === 0 && <Divider />}
                      <Row
                        key={release.id}
                        onClick={() => handleReleaseSelection(release)}
                      >
                        <ArtistEarningItem
                          className="item"
                          key={`${release.id}_${i}`}
                          type={release.type}
                          quantity={release.streams || 0}
                          earned={DineroFactory({
                            amount: release.earnings || 0,
                            precision: 5,
                            currency: 'USD',
                          })}
                          srcImg={release.cover.url}
                          name={release.title}
                          isDarkBg
                          isSelected={selectedReleases.some(
                            (r) => r.id === release.id
                          )}
                        />
                        {selectedReleases.some((r) => r.id === release.id) ? (
                          <MotionOpacity>
                            <CompletedIcon size={32} className="icon" />
                          </MotionOpacity>
                        ) : (
                          <MotionOpacity>
                            <UnselectedIcon isWhite noAnimation />
                          </MotionOpacity>
                        )}
                      </Row>
                      {i !== releasesWithEarnings.length - 1 && <Divider />}
                    </>
                  );
                })}
              </ReleasesWrapper>
            </AnimatePresence>
          )}
        </Wrapper>
      </Box>
      <Hint
        noBorderTop
        customTitle={<Translate id="ARTIST.RESTORE.HINT.TITLE" />}
      >
        <Translate id="ARTIST.RESTORE.HINT.TEXT" />
      </Hint>
      <ButtonsWrapper gap={5} flexDirection={props.isMobile ? 'column' : 'row'}>
        {
          <Button fullWidth buttonType="alert-secondary" onClick={handleCancel}>
            <Translate id="ARTIST.RESTORE.BUTTON.CANCEL" />
          </Button>
        }
        <ButtonLoadingCtrl
          fullWidth
          buttonType="primary"
          className="btn"
          isLoading={restoreStarted}
          onClick={handleContinue}
        >
          <Translate id="ARTIST.RESTORE.BUTTON.CONTINUE" />
        </ButtonLoadingCtrl>
      </ButtonsWrapper>
    </PageContainer>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(RestoreArtistPage)`
  margin: 0 auto;
  padding-top: 30px;

  .header {
    margin-bottom: 20px;
  }
`);

const Wrapper = styled.div`
  display: grid;
  grid-gap: 10px;
`;
const OptionBox = styled.div`
  cursor: pointer;
  padding: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.differentBackground};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  span {
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.primary};
    span {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }

  &.no-border-bottom {
    border-radius: 4px 4px 0 0;
  }

  .icon {
    width: 32px;
    height: 32px;
  }
`;

const ReleasesWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  .item {
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: 20px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    cursor: pointer;
  }
`;

const ButtonsWrapper = styled(Flex)`
  margin-top: 20px;
`;

const PageContainer = styled.div`
  padding: 0 24px 24px;
  width: 100%;
  min-height: calc(100vh - ${({ theme }) => theme.sizes.navbarMobile});

  ${({ theme }) => `@media screen and ${theme.device.mobileS}`} {
    max-width: ${getWidth(12)};
    padding: 0 24px 32px;
    margin: 0 auto;
  }

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    min-height: calc(100vh - ${({ theme }) => theme.sizes.navbar});
    padding-bottom: 60px;
  }
`;

const Badge = styled.div`
  background: ${({ theme }) => theme.colors.blueColor};
  padding: 5px 8px;
  border-radius: 90px;
  margin-left: 5px;

  span {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const Divider = styled(Separator)`
  background-color: grey;
  width: 90%;
`;
